import {ReactNode, useEffect, useState} from "react";
import {Alert, Container} from "react-bootstrap";
import {NavLink} from "react-router-dom";
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import "./AnnualReport.css"
import AnnualReportListing from "./model/AnnualReportListing";
import apiClient from "../../framework/web/ApiClient";

/**
 * Endpoint to fetch report information
 */
const FETCH_FORM_DATA_URL = "/ams/api/v1/annual-report-ui/find-reports";

/**
 * Landing page for annual reports
 */
export default function AnnualReportLandingPage() {
   const [showAll, setShowAll] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [listings, setListings] = useState<AnnualReportListing[]>([]);

   // Show confirmation alert
   let queryParams = new URLSearchParams(window.location.search);
   let isReturnOnSubmitParam = queryParams.get('submit');
   const [showAlert, setShowAlert] = useState(isReturnOnSubmitParam === 'success');

   // Load project info
   useEffect(() => {
      const fetchReportData = async () => {
         try {
            // Prepare request URL
            const response = await apiClient.get(FETCH_FORM_DATA_URL, {});
            if (response.status != 201) {
               const parsedData: AnnualReportListing[] = response.data.data.map((listing: any): AnnualReportListing[] => ({
                  ...listing,
                  // Parse timestamp into date
                  submittedDate: (listing.submittedDate) ? new Date(listing.submittedDate) : null,
                  nextSubmissionDate: (listing.nextSubmissionDate) ? new Date(listing.nextSubmissionDate) : null
               }));
               setListings(parsedData);
            } else {
               setError(`Something went wrong. Response Status ${response.status}, Body ${response.statusText}`);
            }
         } catch (error) {
            setError(`Failed to fetch menu: ${error}`);
         }
      };
      fetchReportData();
   }, [showAlert]);

   /**
    * This method is responsible for displaying the correct status badge
    *
    * @param status The number that represents the status
    * @return A react component with the correct cell to be displayed
    */
   function showStatusBadge(status: number): ReactNode | null {
      switch (status) {
         case 1:
            return <div>
               <i className="bi bi-circle-fill" style={{color: "#A7A7A7"}}/> Not yet due
            </div>
         case 2:
            return <div>
               <i className="bi bi-circle-fill" style={{color: "#14C52F"}}/> Ready for submission
            </div>
         case 3:
            return <div>
               <i className="bi bi-circle-fill" style={{color: "#DE8A15"}}/> Overdue
            </div>
         case 4:
            return <div>
               <i className="bi bi-circle-fill" style={{color: "#D03B3B"}}/> 14+ Days Overdue
            </div>
         case 5:
            return <div>
               <i className="bi bi-circle-fill" style={{color: "#08D1F3"}}/> Draft
            </div>
         case 6:
            return <div>
               <i className="bi bi-circle-fill" style={{color: "#2F99F0"}}/> Waiting Review
            </div>
         case 7:
            return <div>
               <i className="bi bi-circle-fill" style={{color: "#EDBA13"}}/> Feedback
            </div>
         case 8:
            return <div>
               <i className="bi bi-circle-fill" style={{color: "#4C4C4C"}}/> No Feedback
            </div>
      }
   }

   /**
    * This method will show determine which button needs to be shown in the actions column
    *
    * @param listing The relevant listing
    * @param index The index of this row. Used for tag IDs
    */
   function getActionButton(listing: AnnualReportListing, index: number): ReactNode | null {
      switch (listing.status) {
         case 1:
            // None
            return <></>
         case 2:
         case 3:
         case 4:
            // New Report
            return <NavLink id={`annualReportLandingPageTableRowCol6Action_${index}`}
                            to={`?page=annual-report-submit&applicationId=${listing.applicationId}&instituteId=${listing.instituteId}&form=${listing.collabType === 1 ? "applicant" : "collaborator"}`}
                            className="nav-link">
               <button id={`annualReportLandingPageTableRowCol6ActionBtn_${index}`} type="button"
                       className="btn btn-primary action-button">New Report
               </button>
            </NavLink>
         case 5:
         case 7:
            // Edit Report
            return <NavLink id={`annualReportLandingPageTableRowCol6Action_${index}`}
                            to={`?page=annual-report-submit&applicationId=${listing.applicationId}&instituteId=${listing.instituteId}&form=${listing.collabType === 1 ? "applicant" : "collaborator"}&reportId=${listing.projectAnnualReportId}`}
                            className="nav-link">
               <button id={`annualReportLandingPageTableRowCol6ActionBtn_${index}`} type="button"
                       className="btn btn-primary action-button">Edit Report
               </button>
            </NavLink>
         case 6:
         case 8:
            // View Report
            return <NavLink id={`annualReportLandingPageTableRowCol6Action_${index}`}
                            to={`?page=annual-report-submit&applicationId=${listing.applicationId}&instituteId=${listing.instituteId}&form=${listing.collabType === 1 ? "applicant" : "collaborator"}&reportId=${listing.projectAnnualReportId}`}
                            className="nav-link">
               <button id={`annualReportLandingPageTableRowCol6ActionBtn_${index}`} type="button"
                       className="btn btn-primary action-button">View Report
               </button>
            </NavLink>
      }
   }

   function formatDate(relevantDate: Date): String {
      return (relevantDate) ? `${relevantDate.getDate()}/${relevantDate.getMonth() + 1}/${relevantDate.getFullYear()}` : '-';
   }

   if (error) {
      return <div>Error: {error}</div>;
   }

   if (listings.length === 0) {
      return <div>Loading ...</div>
   }

   return (
      <Container id="annualReportLandingPage" fluid>
         <h1 id="annualReportLandingPageTitle">Your annual reports</h1>
         <div id="annualReportLandingPageShowAllDic" className="form-check form-switch">
            <input id="annualReportLandingPageShowAllBtn" className="form-check-input" type="checkbox" role="switch"
                   onClick={() => setShowAll(!showAll)}/>
            <label id="annualReportLandingPageShowAllLabel" className="form-check-label"
                   htmlFor="annualReportLandingPageShowAllBtn">See all reports</label>
         </div>
         <Alert id="annualReportLandingPageAlert" variant="success" className="confirmation-alert" show={showAlert}
                onClose={() => setShowAlert(false)} dismissible>
            <Alert.Heading id="annualReportLandingPageAlertHeading">Report Submitted</Alert.Heading>
            <p id="annualReportLandingPageAlertText">
               Your report has been successfully submitted and will now be reviewed by our Access team.
            </p>
         </Alert>
         <TableContainer id="annualReportLandingPageTableContainer" style={{width: '100%'}}>
            <Table id="annualReportLandingPageTabke" size="small">
               {/*Table Header*/}
               <TableHead id="annualReportLandingPageTableHead">
                  <TableRow id="annualReportLandingPageTableHeadRow">
                     <TableCell id="annualReportLandingPageTableHeadCol1" className="header c1">Project ID</TableCell>
                     <TableCell id="annualReportLandingPageTableHeadCol2" className="header c2">Project
                        Title</TableCell>
                     <TableCell id="annualReportLandingPageTableHeadCol3" className="c3">
                        <div id="annualReportLandingPageTableHeadCol3Div" className="two-row-column">
                           <span id="annualReportLandingPageTableHeadCol3Main" className="main">Institute</span>
                           <span id="annualReportLandingPageTableHeadCol3Sec"
                                 className="secondary">Collaboration Type</span>
                        </div>
                     </TableCell>
                     <TableCell id="annualReportLandingPageTableHeadCol4" className="header c4">Status</TableCell>
                     <TableCell id="annualReportLandingPageTableHeadCol5" className="c3">
                        <div id="annualReportLandingPageTableHeadCol5Div" className="two-row-column">
                           <span id="annualReportLandingPageTableHeadCol5Main" className="main">Due by</span>
                           <span id="annualReportLandingPageTableHeadCol5Sec"
                                 className="secondary">Submitted on</span>
                        </div>
                     </TableCell>
                     <TableCell id="annualReportLandingPageTableHeadCol6" className="header c6">Actions</TableCell>
                  </TableRow>
               </TableHead>
               {/*Table body*/}
               <TableBody id="annualReportLandingPageTableBody">
                  {listings.map((row, index) => ((!showAll && (row.status === 1 || row.status === 6 || row.status === 8)) ?
                        null :
                        <TableRow id={`annualReportLandingPageTableRow_${index}`} key={index}
                                  className={(row.status === 1 || row.status === 6 || row.status === 8) ? 'not-submittable' : ''}>
                           <TableCell id={`annualReportLandingPageTableRowCol1_${index}`}
                                      className="data c1">{row.applicationId}</TableCell>
                           <TableCell id={`annualReportLandingPageTableRowCol2_${index}`}
                                      className="data c2">{row.projectTitle}</TableCell>
                           <TableCell id={`annualReportLandingPageTableRowCol3_${index}`} className="data c3">
                              <div id={`annualReportLandingPageTableRowCol3Div_${index}`} className="two-row-column">
                                 <span id={`annualReportLandingPageTableRowCol3Inst_${index}`}
                                       className="main data">{row.instituteId} - {row.instituteName}</span>
                                 <span id={`annualReportLandingPageTableRowCol3CollabType_${index}`}
                                       className="secondary">{row.collabType === 1 ? 'Applicant' : 'Collaborator'}</span>
                              </div>
                           </TableCell>
                           <TableCell id={`annualReportLandingPageTableRowCol4_${index}`}
                                      className="data c4">{showStatusBadge(row.status)}</TableCell>
                           <TableCell id={`annualReportLandingPageTableRowCol5_${index}`} className="data c3">
                              <div id={`annualReportLandingPageTableRowCol5Div_${index}`} className="two-row-column">
                                 <span id={`annualReportLandingPageTableRowCol5DueDate_${index}`}
                                       className="main data">{`${formatDate(row.nextSubmissionDate)}`}</span>
                                 <span id={`annualReportLandingPageTableRowCol5SubmissionDate_${index}`}
                                       className="secondary">{`${formatDate(row.submittedDate)}`}</span>
                              </div>
                           </TableCell>
                           <TableCell id={`annualReportLandingPageTableRowCol6_${index}`}
                                      className="data c6">{getActionButton(row, index)}</TableCell>
                        </TableRow>
                  ))}
               </TableBody>
            </Table>
         </TableContainer>
      </Container>
   );
}
